import { StandardEvents } from '@shopify/events';

class BrzPurchase extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    const { signal } = this.abort;
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.form = this.querySelector('product-form-component form');
    this.planSelect = this.querySelector('[data-plan]');
    this.picker = this.querySelector('[data-picker]');
    this.offers = [...this.querySelectorAll('[data-offer]')];
    this.planSelect?.removeAttribute('name');
    if (!this.form || this.dataset.mode === 'app') return;
    this.mode = this.dataset.defaultPurchase || 'once';
    if (!this.querySelector('[data-purchase="once"]')) this.mode = 'subscription';
    if (!this.planSelect?.options.length) this.mode = 'once';
    const existing = this.form.querySelector('input[name="selling_plan"]');
    this.planInput = existing || document.createElement('input');
    if (!existing) { this.planInput.type = 'hidden'; this.planInput.name = 'selling_plan'; this.planInput.dataset.brzPlan = ''; this.form.append(this.planInput); }
    const selected = this.offers.find(offer => offer.querySelector('input[type=radio]')?.checked) || this.offers[0];
    if (selected) selected.querySelector('input[type=radio]').checked = true;
    this.addEventListener('change', event => {
      if (event.target.matches('.brz-offer input[type=radio], [data-plan]')) this.sync();
    }, { signal });
    this.addEventListener('click', event => {
      const tab = event.target.closest('[data-purchase]');
      if (tab) { this.mode = tab.dataset.purchase; this.sync(); }
    }, { signal });
    document.addEventListener(StandardEvents.productSelect, event => {
      if (!event.target.closest(`#shopify-section-${CSS.escape(this.dataset.section)}`)) return;
      const generation = this.generation = (this.generation || 0) + 1;
      this.variantPending = true;
      event.promise?.then(() => {
        // Native listeners complete their form updates before this queued synchronization.
        requestAnimationFrame(() => {
          if (!this.isConnected || generation !== this.generation) return;
          this.variantPending = false; this.sync();
        });
      }).catch(() => { this.variantPending = false; });
    }, { signal });
    this.form.addEventListener('submit', event => {
      this.sync();
      if (this.invalid || this.variantPending) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, { capture: true, signal });
    this.sync();
  }
  money(cents) {
    // Shopify Ajax/Liquid presentment prices use hundredths, including zero-decimal currencies.
    return new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
  }
  sync() {
    if (!this.form || this.dataset.mode !== 'native') return;
    const variantId = this.form.querySelector('[name=id]')?.value;
    const variant = this.variants.find(v => String(v.id) === String(variantId));
    if (!variant) return;
    this.querySelectorAll('[data-purchase]').forEach(tab => tab.setAttribute('aria-pressed', String(tab.dataset.purchase === this.mode)));
    this.querySelector('[data-subscription-info]').hidden = this.mode !== 'subscription';
    const planIds = new Set(variant.plans.map(plan => String(plan.id)));
    if (this.planSelect) {
      [...this.planSelect.options].forEach(option => { option.disabled = !planIds.has(option.value); });
      if (!planIds.has(this.planSelect.value)) this.planSelect.value = String(variant.plans[0]?.id || '');
    }
    const plan = this.mode === 'subscription' ? variant.plans.find(p => String(p.id) === this.planSelect?.value) : null;
    this.planInput.disabled = this.mode !== 'subscription';
    this.planInput.value = plan ? String(plan.id) : '';
    this.invalid = !variant.available || (this.mode === 'subscription' && !plan);
    const selected = this.offers.find(offer => offer.querySelector('input:checked'));
    const quantity = selected ? Number(selected.dataset.quantity) : variant.min;
    if (quantity < variant.min || (quantity - variant.min) % variant.step !== 0 || (variant.max && quantity > variant.max)) this.invalid = true;
    const quantityInput = this.form.querySelector('[name=quantity]');
    if (quantityInput && quantityInput.value !== String(quantity)) {
      quantityInput.value = String(quantity);
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // Keep Horizon's variant picker mounted once; its native listeners survive bundle changes.
    const unitPrice = plan ? plan.price : variant.price;
    const comparePrice = plan ? plan.compare : variant.compare;
    this.offers.forEach(offer => {
      const qty = Number(offer.dataset.quantity);
      offer.querySelector('[data-offer-price]').textContent = this.money(unitPrice * qty);
      const compare = offer.querySelector('[data-offer-compare]');
      compare.hidden = comparePrice <= unitPrice;
      compare.textContent = comparePrice > unitPrice ? this.money(comparePrice * qty) : '';
      const invalidQty = qty < variant.min || (qty - variant.min) % variant.step !== 0 || (variant.max && qty > variant.max);
      offer.querySelector('input[type=radio]').disabled = Boolean(invalidQty);
    });
    const submit = this.form.querySelector('[name=add]');
    if (submit) submit.disabled = Boolean(this.invalid);
    this.dispatchEvent(new CustomEvent('bruzlo:purchase', { bubbles: true, detail: { total: this.money(unitPrice * quantity), disabled: this.invalid } }));
  }
  disconnectedCallback() { this.abort?.abort(); }
}
class BrzSticky extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    const { signal } = this.abort;
    this.section = this.closest('.brz-product');
    this.target = this.section?.querySelector('product-form-component [name=add]');
    if (!this.target) return;
    this.querySelector('[data-sticky-submit]').addEventListener('click', () => {
      this.target = this.section.querySelector('product-form-component [name=add]');
      this.target?.click();
    }, { signal });
    this.section.addEventListener('bruzlo:purchase', event => {
      this.querySelector('[data-sticky-price]').textContent = event.detail.total;
      this.querySelector('button').disabled = Boolean(event.detail.disabled);
    }, { signal });
    this.observer = new IntersectionObserver(entries => {
      this.pastButton = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
      this.update();
    });
    this.observer.observe(this.target);
    this.mutation = new MutationObserver(() => {
      const target = this.section.querySelector('product-form-component [name=add]');
      if (target && target !== this.target) { this.observer.unobserve(this.target); this.target = target; this.observer.observe(target); }
      this.querySelector('button').disabled = this.target.disabled;
    });
    this.mutation.observe(this.section.querySelector('.brz-native-buy'), { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled'] });
    const footer = document.querySelector('footer');
    if (footer) {
      this.footerObserver = new IntersectionObserver(entries => { this.atFooter = entries[0].isIntersecting; this.update(); });
      this.footerObserver.observe(footer);
    }
  }
  update() { this.hidden = !this.pastButton || this.atFooter; }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.mutation?.disconnect(); this.footerObserver?.disconnect(); }
}
if (!customElements.get('brz-purchase')) customElements.define('brz-purchase', BrzPurchase);
if (!customElements.get('brz-sticky')) customElements.define('brz-sticky', BrzSticky);
